import { LoadingButton } from '@mui/lab';
import { Box, Button } from '@mui/material';
import { FC, useCallback } from 'react';

import { BoxRow } from 'src/components/BoxRow';
import { Dialog } from 'src/components/Dialog';
import { Spacer } from 'src/components/Spacer';
import { useAlert } from 'src/services/alert/useAlert';
import { isEmpty, isNil, maybeCall } from 'src/utils/common';
import { theme } from '../../theme';
import { useUpdateUsersBulkMutation } from '../api';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (role: string) => void;
  userIds: number[];
  role: string;
}

export const ConfirmAdminRoleModal: FC<Props> = ({
  isOpen,
  onClose,
  onSuccess,
  userIds,
  role,
}) => {
  const { onOpenAlertDelayed } = useAlert();

  const [updateRole, { isLoading: isLoadingUpdateRole }] =
    useUpdateUsersBulkMutation();

  const onSubmit = useCallback(async () => {
    try {
      await updateRole({
        role,
        userIds,
      }).unwrap();

      maybeCall(onClose);
      maybeCall(onSuccess, role);

      onOpenAlertDelayed({
        message: 'Role for selected users was successfully updated.',
        title: 'Role updated!',
        type: 'success',
      });
    } catch (err: any) {
      onOpenAlertDelayed({
        message: err?.data?.message === 'AT_LEAST_ONE_SUPER_ADMIN_REQUIRED'
          ? 'At least one Super Admin must remain in the system.'
          : 'There was an error, please try again.',
        title: 'Oops!',
        type: 'error',
      });
    } finally {
      onClose();
    }
  }, [role, userIds, updateRole, onClose]);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      sx={{
        padding: '25px 100px',
        [theme.breakpoints.down('mobile')]: {
          margin: '15px',
        },
      }}
      title="Confirm Admin Role Change"
    >
      <Box sx={{ marginBottom: '30px', color: '#356482', lineHeight: '1.6' }}>
        Are you sure you want to change the user role to Admin? This will give them full access to user data.
      </Box>
      <BoxRow sx={{ marginTop: '15px', width: '100%' }}>
        <Button
          onClick={onClose}
          sx={{ flex: 1, height: '40px' }}
          variant="outlined"
        >
          Cancel
        </Button>
        <Spacer width="20px" />
        <LoadingButton
          disabled={isEmpty(userIds) || isLoadingUpdateRole}
          loading={isLoadingUpdateRole}
          onClick={onSubmit}
          sx={{ flex: 1, height: '40px' }}
          variant="contained"
        >
          Confirm
        </LoadingButton>
      </BoxRow>
    </Dialog>
  );
};
